/**
 * MessageBubble — one SMS/Slack row in the Activity timeline. Outbound texts
 * and Slack posts sit right-aligned on navy, inbound texts left on the page
 * colour; the status chip carries the queue outcome (queued / dry run / sent /
 * failed). A Slack post's meta line reads "Slack · #channel" — the name comes
 * from the channels list when the caller has it, else the raw channel id.
 */
import { MessageSquare, Slack } from 'lucide-react';
import { fullDateTime, relativeTime } from '@/lib/time';
import type { Message } from '@/features/pipeline/types';
import { messageStatusMeta } from './activity-merge';
import { nudgeMessageKindLabel } from './nudge-format';

export function MessageBubble({
  message,
  slackChannelName,
}: {
  message: Message;
  /** `id → "#name"`; absent or `undefined` for an id falls back to the id itself. */
  slackChannelName?: (id: string) => string | undefined;
}) {
  const outbound = message.direction === 'outbound';
  const slack = message.channel === 'slack';
  const status = messageStatusMeta(message.status);
  const kindLabel = nudgeMessageKindLabel(message.kind);
  const Icon = slack ? Slack : MessageSquare;
  const counterpart = outbound ? message.to_address : message.from_address;
  const where = slack
    ? `Slack · ${counterpart ? slackChannelName?.(counterpart) ?? counterpart : '—'}`
    : `Text ${outbound ? 'to' : 'from'} ${counterpart || '—'}`;
  // The timeline places the bubble by `created_at`, so that is the time it shows; the
  // moment it actually went out is the `message_sent` event row above it (QA stage 3).
  const when = message.created_at;

  return (
    <div
      className={`flex flex-col ${outbound ? 'items-end' : 'items-start'}`}
      data-testid={`message-${message.id}`}
      data-direction={message.direction}
      data-channel={message.channel}
    >
      <div
        className={[
          'max-w-[88%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-5 whitespace-pre-wrap break-words',
          outbound ? 'rounded-br-md bg-navy text-white' : 'rounded-bl-md bg-page text-gray-900',
        ].join(' ')}
      >
        {message.body}
      </div>
      <div className={`mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px] text-muted ${outbound ? 'justify-end' : ''}`}>
        <Icon size={12} strokeWidth={2} aria-hidden />
        {kindLabel && (
          <>
            <span className="font-medium text-gray-700" data-testid="message-kind">{kindLabel}</span>
            <span aria-hidden> · </span>
          </>
        )}
        <span data-testid="message-where">{where}</span>
        <span aria-hidden> · </span>
        <time dateTime={when} title={fullDateTime(when)}>
          {relativeTime(when)}
        </time>
        {outbound && (
          <span className={`rounded-full px-2 py-0.5 font-medium ${status.chip}`} data-testid="message-status">
            {status.label}
          </span>
        )}
      </div>
      {message.status === 'failed' && message.error && (
        <p className="mt-0.5 text-[12px] text-danger">{message.error}</p>
      )}
      {message.status === 'queued' && message.scheduled_for && (
        <p className="mt-0.5 text-[12px] text-muted">
          Sends {relativeTime(message.scheduled_for).replace(' ago', '')}
          {' · '}
          <time dateTime={message.scheduled_for}>{fullDateTime(message.scheduled_for)}</time>
        </p>
      )}
    </div>
  );
}
